import { useEffect, useState } from 'react';
import axios from 'axios';
import { Plane, Car, Hotel, MapPin, ExternalLink, TrainFront, Clock, Star, Phone } from 'lucide-react';
import SiteHeader from './SiteHeader';
import SiteFooter from './SiteFooter';
import { trackPageView } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CAMP_ADDRESS = '7260 Mickey Smith Parkway, Webster, WI 54893';
const CAMP_QUERY = encodeURIComponent(CAMP_ADDRESS);

const TERMINALS = [
  {
    name: 'Terminal 1 · Lindbergh',
    airlines: 'Delta, American, United, Alaska, Air Canada, Air France, KLM, Lufthansa, Aer Lingus, WestJet',
    tip: 'The big one. Most flights land here. Ground transportation and rideshare pickup are on the Ground Transport level.',
  },
  {
    name: 'Terminal 2 · Humphrey',
    airlines: 'Sun Country, Southwest, Frontier, Icelandair',
    tip: 'Smaller and quicker. Rideshare pickup is in the Purple Ramp, right across from baggage claim.',
  },
];

const Travel = () => {
  const [hotels, setHotels] = useState([]);
  useEffect(() => {
    trackPageView('Travel');
    axios.get(`${BACKEND_URL}/api/reserve/travel`).then(r => setHotels(r.data.hotels || [])).catch(() => setHotels([]));
  }, []);

  return (
    <div className="min-h-screen bg-cream-50" data-testid="travel-page">
      <SiteHeader active="travel" />

      <section className="relative overflow-hidden bg-[#1a0d10] text-white py-16 md:py-24 px-4 md:px-6">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(124,29,51,0.25),transparent_55%),radial-gradient(ellipse_at_bottom_left,rgba(217,161,91,0.12),transparent_60%)]" />
        <div className="absolute inset-0 grain" />
        <div className="max-w-5xl mx-auto relative z-10">
          <p className="eyebrow text-white/50 mb-5">Getting here</p>
          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl font-medium leading-[0.95] tracking-tighter mb-6">
            Travel <span className="bg-gradient-to-br from-[#c98a4b] via-[#d9a15b] to-[#f0d3a0] bg-clip-text text-transparent italic">information.</span>
          </h1>
          <p className="text-sm md:text-base text-white/70 max-w-2xl leading-relaxed">
            Fly into Minneapolis–St. Paul, stay the night in Bloomington if you land late, then it's about 90 minutes north to Herzl Camp on the Wisconsin side.
          </p>
          <div className="mt-8 flex flex-wrap gap-6 text-sm text-white/85">
            <span className="inline-flex items-center gap-2"><Plane className="w-4 h-4 text-[#d9a15b]" />MSP Airport</span>
            <span className="inline-flex items-center gap-2"><Car className="w-4 h-4 text-[#d9a15b]" />~90 min drive to camp</span>
            <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-[#d9a15b]" />Herzl Camp · Webster, WI</span>
          </div>
        </div>
      </section>

      {/* Map + address */}
      <section className="py-14 md:py-20 px-4 md:px-6" data-testid="travel-map-section">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[1fr,1.2fr] gap-8 lg:gap-12 items-stretch">
          <div className="flex flex-col justify-center">
            <p className="eyebrow text-gray-400 mb-3">The destination</p>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900 mb-4">Herzl Camp</h2>
            <p className="text-gray-600 leading-relaxed mb-6">Tucked into the pines on Devils Lake in northwest Wisconsin. Cell service is spotty once you're close — download directions before you leave the hotel.</p>
            <div className="rounded-2xl bg-white border border-gray-200 shadow-sm p-5 mb-5">
              <p className="text-[10px] font-semibold tracking-[0.25em] uppercase text-gray-400 mb-2">Camp address</p>
              <p className="font-semibold text-gray-900">Herzl Camp</p>
              <p className="text-gray-700">7260 Mickey Smith Parkway</p>
              <p className="text-gray-700">Webster, WI 54893</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <a href={`https://www.google.com/maps/dir/?api=1&destination=${CAMP_QUERY}`} target="_blank" rel="noopener noreferrer" data-testid="travel-directions-link"
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-wine to-burgundy text-white px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity">
                Get directions <ExternalLink className="w-4 h-4" />
              </a>
              <a href={`https://www.google.com/maps/dir/?api=1&origin=MSP+Airport&destination=${CAMP_QUERY}`} target="_blank" rel="noopener noreferrer" data-testid="travel-msp-directions-link"
                className="inline-flex items-center gap-2 rounded-full border border-gray-300 text-gray-700 px-5 py-2.5 text-sm font-semibold hover:bg-white transition-colors">
                Route from MSP <Car className="w-4 h-4" />
              </a>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-xl min-h-[340px] bg-gray-100">
            <iframe
              title="Herzl Camp map"
              src={`https://www.google.com/maps?q=${CAMP_QUERY}&z=9&output=embed`}
              className="w-full h-full min-h-[340px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              data-testid="travel-map-embed"
            />
          </div>
        </div>
      </section>

      {/* Airport */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-white border-y border-gray-100" data-testid="travel-airport-section">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-10">
            <p className="eyebrow text-gray-400 mb-3">Flying in</p>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900 mb-4">Minneapolis–St. Paul International (MSP)</h2>
            <p className="text-gray-600 leading-relaxed">MSP has two terminals that are <strong>not</strong> connected by walkway — check your airline before you book a ride. A free light-rail hop (METRO Blue Line) connects them around the clock.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {TERMINALS.map((t, i) => (
              <div key={t.name} className={`rounded-2xl p-6 md:p-7 border ${i === 0 ? 'bg-[#1a0d10] text-white border-[#d9a15b]/30' : 'bg-cream-50 border-cream-200 text-gray-900'}`} data-testid={`terminal-card-${i + 1}`}>
                <div className="flex items-center gap-3 mb-4">
                  <span className={`w-10 h-10 rounded-xl flex items-center justify-center font-display text-2xl ${i === 0 ? 'bg-[#d9a15b]/15 text-[#d9a15b]' : 'bg-wine-50 text-wine'}`}>{i + 1}</span>
                  <h3 className="font-display text-2xl font-semibold">{t.name}</h3>
                </div>
                <p className={`text-[10px] font-semibold tracking-[0.25em] uppercase mb-1.5 ${i === 0 ? 'text-white/50' : 'text-gray-400'}`}>Airlines</p>
                <p className={`text-sm leading-relaxed mb-4 ${i === 0 ? 'text-white/85' : 'text-gray-800'}`}>{t.airlines}</p>
                <p className={`text-sm leading-relaxed ${i === 0 ? 'text-white/65' : 'text-gray-600'}`}>{t.tip}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 grid sm:grid-cols-2 gap-4 text-sm">
            <div className="flex gap-3 rounded-xl bg-cream-50 border border-cream-200 p-4 text-gray-700"><TrainFront className="w-5 h-5 text-wine shrink-0 mt-0.5" /><p><strong>Between terminals:</strong> ride the METRO Blue Line one stop — free for airport transfers, runs 24 hours. Follow "Light Rail" signs from the Tram Level (T1) or the skyway (T2).</p></div>
            <div className="flex gap-3 rounded-xl bg-cream-50 border border-cream-200 p-4 text-gray-700"><Clock className="w-5 h-5 text-wine shrink-0 mt-0.5" /><p><strong>Timing:</strong> the DoubleTree is ~10 minutes from either terminal; camp is ~90 minutes from the hotel. Aim to land by early afternoon on arrival day if you're driving straight up.</p></div>
          </div>
        </div>
      </section>

      {/* Hotels */}
      <section className="py-14 md:py-20 px-4 md:px-6" data-testid="travel-hotels-section">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-10">
            <p className="eyebrow text-gray-400 mb-3">Where to stay</p>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900 mb-4">Landing the night before?</h2>
            <p className="text-gray-600 leading-relaxed">Bloomington sits right next to the airport, and our caravan to camp leaves from the DoubleTree on Normandale Blvd — stay there if you want the easiest hand-off.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {hotels.map((h, i) => (
              <div key={i} className={`relative rounded-2xl bg-white border p-6 md:p-7 shadow-sm transition-shadow hover:shadow-lg ${h.recommended ? 'border-[#d9a15b]/60 ring-1 ring-[#d9a15b]/30' : 'border-gray-200'}`} data-testid={`hotel-card-${i}`}>
                {h.recommended && <span className="absolute top-4 right-4 inline-flex items-center gap-1 rounded-full bg-[#d9a15b]/15 text-[#a86f34] text-[10px] font-bold tracking-widest uppercase px-3 py-1"><Star className="w-3 h-3" />Recommended</span>}
                <div className="w-10 h-10 rounded-xl bg-wine-50 text-wine flex items-center justify-center mb-4"><Hotel className="w-5 h-5" /></div>
                <h3 className="font-display text-2xl font-semibold text-gray-900 mb-1 pr-28">{h.name}</h3>
                {h.area && <p className="text-sm text-gray-500 mb-2">{h.area}</p>}
                {h.distance && <p className="text-xs font-medium text-wine mb-3">{h.distance}</p>}
                {h.description && <p className="text-sm text-gray-600 leading-relaxed">{h.description}</p>}
                <div className="flex flex-wrap gap-3 mt-4">
                  {h.url && <a href={h.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-wine hover:underline">View on map <ExternalLink className="w-3.5 h-3.5" /></a>}
                  {h.phone && <a href={`tel:${h.phone}`} className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"><Phone className="w-3.5 h-3.5" />{h.phone}</a>}
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-8">Questions about rides or the caravan? Email <a href="mailto:info@rojoretreat.com" className="underline text-wine">info@rojoretreat.com</a>.</p>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default Travel;
